/**
 * LyricOrchestratorPanel — Main deliberation display for Parliament Write.
 * Shows progress during drafting, synthesis result, and agent perspectives.
 */

import React from 'react';
import { X, Users } from 'lucide-react';
import { Button, StatusDot, LoadingSpinner, Badge } from '@/components/ui';
import { LYRIC_WRITER_AGENTS } from '@/data/lyricOrchestratorAgents';
import { LyricDraftCard } from './LyricDraftCard';
import { LyricSynthesisDisplay } from './LyricSynthesisDisplay';
import type {
  LyricDeliberationResult,
  LyricDeliberationStatus,
  LyricProgressEvent,
} from '@/types/lyricOrchestrator.types';

interface LyricOrchestratorPanelProps {
  status: LyricDeliberationStatus;
  isWriting: boolean;
  result: LyricDeliberationResult | null;
  error: string | null;
  progress: LyricProgressEvent | null;
  completedAgents: string[];
  onCancel: () => void;
  onReset: () => void;
  onAccept: () => void;
}

function getProgressText(progress: LyricProgressEvent | null, status: LyricDeliberationStatus): string {
  if (status === 'synthesizing') return 'The Parliament Writer is merging the best sections...';
  if (!progress) return 'Starting Parliament Write...';

  switch (progress.type) {
    case 'drafting-start':
      return '4 specialist writers are drafting lyrics in parallel...';
    case 'agent-complete':
      return `Writer completed: ${LYRIC_WRITER_AGENTS.find(a => a.id === progress.agentId)?.name || progress.agentId}`;
    case 'agent-failed':
      return `Writer failed: ${progress.error}`;
    case 'drafting-complete':
      return `All drafts complete (${progress.successCount} writers succeeded)`;
    case 'synthesis-start':
      return 'Merging the best sections from all drafts...';
    case 'synthesis-complete':
      return 'Merged lyrics ready!';
    case 'error':
      return progress.message;
    default:
      return 'Processing...';
  }
}

export const LyricOrchestratorPanel: React.FC<LyricOrchestratorPanelProps> = ({
  status,
  isWriting,
  result,
  error,
  progress,
  completedAgents,
  onCancel,
  onReset,
  onAccept,
}) => {
  return (
    <div className="space-y-6 bg-slate-900/40 p-6 rounded-2xl border-l-4 border-l-accent shadow-2xl">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Users size={18} className="text-amber-400" />
          <h3 className="text-sm font-black text-slate-200 uppercase tracking-wider">
            Parliament Write
          </h3>
          {result && <Badge variant="success" size="xs">Complete</Badge>}
          {status === 'failed' && <Badge variant="danger" size="xs">Failed</Badge>}
          {status === 'aborted' && <Badge variant="warning-muted" size="xs">Cancelled</Badge>}
        </div>
        <div className="flex items-center gap-2">
          {isWriting && (
            <Button
              variant="outline"
              size="sm"
              onClick={onCancel}
              leftIcon={<X size={14} />}
              className="text-red-400 border-red-500/30 hover:bg-red-900/20"
            >
              Cancel
            </Button>
          )}
          {(status === 'complete' || status === 'failed' || status === 'aborted') && (
            <Button variant="outline" size="sm" onClick={onReset} className="text-slate-400">
              Dismiss
            </Button>
          )}
        </div>
      </div>

      {/* Progress while writing */}
      {isWriting && (
        <div className="space-y-3 bg-slate-900/50 rounded-lg p-4 border border-slate-800">
          <div className="flex items-center gap-2">
            <LoadingSpinner size="sm" />
            <span className="text-sm text-slate-400">
              {getProgressText(progress, status)}
            </span>
          </div>

          {/* Agent status dots */}
          <div className="flex flex-wrap gap-3">
            {LYRIC_WRITER_AGENTS.map(agent => {
              const isComplete = completedAgents.includes(agent.id);
              return (
                <div key={agent.id} className="flex items-center gap-1.5">
                  <StatusDot
                    color={isComplete ? 'success' : 'warning'}
                    size="sm"
                    pulse={!isComplete}
                  />
                  <span className={`text-[11px] ${isComplete ? 'text-slate-400' : 'text-slate-500'}`}>
                    {agent.name}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Error display */}
      {error && (
        <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-4 text-sm text-red-400">
          {error}
        </div>
      )}

      {/* Results */}
      {result && (
        <>
          {/* Synthesis at top */}
          <LyricSynthesisDisplay synthesis={result.synthesis} onAccept={onAccept} />

          {/* Agent drafts below */}
          <div>
            <h4 className="text-xs font-black text-slate-500 uppercase tracking-widest mb-3">
              Writer Drafts
              <span className="text-slate-600 ml-2">
                ({result.drafts.length} writers, {(result.totalDurationMs / 1000).toFixed(1)}s)
              </span>
            </h4>
            <div className="space-y-2">
              {result.drafts.map(draft => {
                const agent = LYRIC_WRITER_AGENTS.find(a => a.id === draft.agentId);
                if (!agent) return null;
                return (
                  <LyricDraftCard key={draft.agentId} draft={draft} agent={agent} />
                );
              })}
            </div>
          </div>

          {/* Metadata footer */}
          <div className="flex items-center gap-3 text-[10px] text-slate-600 border-t border-slate-800 pt-3">
            <span>{result.agentCount} writers</span>
            <span className="text-slate-800">&middot;</span>
            <span>{(result.totalDurationMs / 1000).toFixed(1)}s</span>
            {result.failedAgents.length > 0 && (
              <>
                <span className="text-slate-800">&middot;</span>
                <span className="text-red-500">{result.failedAgents.length} failed</span>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
};
